// src/modules/app-views/products/pages/Document/tabs/RecordTab/ClassificationCard.jsx
// *******************************************************************************
// CLASSIFICATION CARD
// *******************************************************************************
// Product purpose, sales channels, and product tags.
// Channels only enabled when purpose is sellable (resale/finished_goods/digital).
// Tags rendered with their configured colors.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Col, Row, Form, Tag } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { openModal } from 'components/core/GenericModal';
import ProductsParametersModal from 'modules/app-views/products/pages/Init/modals/ProductsParametersModal';
import { useAppStore } from 'stores/app';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const SELLABLE_PURPOSES = ['resale', 'finished_goods', 'digital'];

// Labels use i18n keys — SimpleSelect's buildOptions translates them automatically
const PURPOSE_OPTIONS = [
  { value: 'resale', label: 'purpose_resale' },
  { value: 'raw_material', label: 'purpose_raw_material' },
  { value: 'semi_finished', label: 'purpose_semi_finished' },
  { value: 'finished_goods', label: 'purpose_finished_goods' },
  { value: 'consumption', label: 'purpose_consumption' },
  { value: 'packaging', label: 'purpose_packaging' },
  { value: 'assembly_part', label: 'purpose_assembly_part' },
  { value: 'digital', label: 'purpose_digital' },
];

const CHANNEL_OPTIONS = [
  { value: 'store', label: 'store_channel' },
  { value: 'web', label: 'web_channel' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ClassificationCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext() || {};
  const storedParameters = useZustand('app', 'parameters', false);
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const purpose = Form.useWatch(['stock', 'purpose'], form) || 'resale';
  const isSellable = SELLABLE_PURPOSES.includes(purpose);

  const moduleId = storedModule?.id || 'products';
  const tagsParams = storedParameters?.modules?.[moduleId]?.tags || [];

  // Build tag options with colored labels for the dropdown
  const tagOptions = useMemo(() => {
    return tagsParams
      .filter(t => !t.removed)
      .map(t => ({
        value: t.uid,
        label: (
          <Tag color={t.color} style={{ margin: 0 }}>{t.label}</Tag>
        ),
        text: t.label, // for search filtering
      }));
  }, [tagsParams]);

  // Custom tag render for selected tags (shows colored Tag component)
  const tagRender = useMemo(() => {
    // Build lookup map for tag params
    const tagMap = {};
    tagsParams.forEach(t => { tagMap[t.uid] = t; });

    return ({ value, closable, onClose }) => {
      const tagParam = tagMap[value];
      if (!tagParam) return <Tag closable={closable} onClose={onClose}>{value}</Tag>;
      return (
        <Tag color={tagParam.color} closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
          {tagParam.label}
        </Tag>
      );
    };
  }, [tagsParams]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handlePurposeChange = value => {
    if (!form) return;
    // Clear channels when switching to non-sellable purpose
    if (!SELLABLE_PURPOSES.includes(value)) {
      form.setFieldValue(['stock', 'channels'], []);
    }
    // Digital products: reset tracking to 'none'
    if (value === 'digital') {
      form.setFieldValue(['stock', 'tracking'], 'none');
      form.setFieldValue(['stock', 'isUnique'], false);
      form.setFieldValue(['stock', 'hasVariants'], false);
      form.setFieldValue(['stock', 'trackInventory'], false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={<div className="text-muted">{translateX('classification')}</div>}>
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={12} lg={8}>
          <SimpleSelect
            name={['stock', 'purpose']}
            label="product_purpose"
            options={PURPOSE_OPTIONS}
            required
            onChange={handlePurposeChange}
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <SimpleSelect
            name={['stock', 'channels']}
            label="sales_channels"
            options={CHANNEL_OPTIONS}
            mode="multiple"
            disabled={!isSellable}
            allowClear
          />
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <SimpleSelect
            name="tags"
            label="tags"
            options={tagOptions}
            mode="multiple"
            allowClear
            showSearch
            useCustomLabel
            tagRender={tagRender}
            showAddButton
            onAddButtonFn={() => openModal(ProductsParametersModal, {
              initialTab: 'tags',
              afterClose: () => useAppStore.getState().refreshParameters?.(),
            })}
          />
        </Col>
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ClassificationCard;
